import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';
import { getMessageCitations } from '../../../services/embeddingsService';

const MessageList = ({ messages, currentUserId, agent, typingUsers = [] }) => {
  const [citations, setCitations] = useState({});

  // Load citations for messages
  useEffect(() => {
    const loadCitations = async () => {
      const citationMap = {};
      
      for (const message of messages) {
        if (message?.senderType === 'agent') {
          try {
            const messageCitations = await getMessageCitations(message?.id);
            if (messageCitations?.length > 0) {
              citationMap[message?.id] = messageCitations;
            }
          } catch (error) {
            console.error('Error loading citations:', error);
          }
        }
      }
      
      setCitations(citationMap);
    };

    if (messages?.length > 0) {
      loadCitations();
    }
  }, [messages]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      {messages?.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">
          <Icon name="MessageCircle" size={48} className="mx-auto mb-3 opacity-50" />
          <p>No messages yet. Start the conversation!</p>
        </div>
      ) : (
        messages?.map((message) => {
          const isUser = message?.senderType === 'user';
          const messageCitations = citations?.[message?.id] || [];
          
          return (
            <div
              key={message?.id}
              className={cn(
                'flex gap-3',
                isUser ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              {/* Avatar */}
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                isUser ? 'bg-primary' : 'bg-secondary'
              )}>
                <Icon
                  name={isUser ? 'User' : 'Bot'}
                  size={16}
                  color={isUser ? 'var(--color-primary-foreground)' : 'var(--color-secondary-foreground)'}
                />
              </div>

              {/* Message Content */}
              <div className={cn(
                'flex flex-col max-w-[70%]',
                isUser ? 'items-end' : 'items-start'
              )}>
                <div className={cn(
                  'rounded-lg px-4 py-2',
                  isUser
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground'
                )}>
                  {!isUser && (
                    <div className="text-xs font-semibold mb-1 opacity-80">
                      {agent?.name}
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message?.content}
                  </p>
                  {message?.isStreaming && (
                    <div className="flex items-center gap-1 mt-2 text-xs opacity-70">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <span className="ml-1">Streaming...</span>
                    </div>
                  )}
                </div>

                {/* Citations */}
                {messageCitations?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {messageCitations?.map((citation, index) => (
                      <div
                        key={citation?.id}
                        className="flex items-center gap-2 px-2 py-1 bg-secondary/50 rounded text-xs"
                      >
                        <Icon name="FileText" size={12} color="var(--color-muted-foreground)" />
                        <span className="text-muted-foreground">
                          {citation?.documentName} ({citation?.documentType})
                        </span>
                        <span className="text-muted-foreground opacity-70">
                          {(citation?.relevanceScore * 100)?.toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Timestamp and Tags */}
                <div className="flex items-center gap-2 mt-1 px-1">
                  <span className="text-xs text-muted-foreground">
                    {formatTime(message?.createdAt)}
                  </span>
                  {message?.tags && message?.tags?.length > 0 && (
                    <div className="flex gap-1">
                      {message?.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Typing Indicators */}
      {typingUsers?.length > 0 && (
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-secondary">
            <Icon name="Bot" size={16} color="var(--color-secondary-foreground)" />
          </div>
          <div className="flex flex-col items-start">
            <div className="rounded-lg px-4 py-2 bg-muted text-foreground">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {typingUsers?.map(u => u?.userName)?.join(', ')} {typingUsers?.length === 1 ? 'is' : 'are'} typing...
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;